/* eslint-disable no-param-reassign */
import axios from 'axios';
import { fetch } from 'whatwg-fetch';

import { getAuthData, saveAuthData, clearAuthData } from './authStorage';
import { API_URL } from '../constants/main';
import localTimeZone from './localTimeZone';
import { useUiStore } from '../store';

axios.defaults.baseURL = API_URL;

let refreshPromise = null;

function refreshTokenOnce() {
  if (!refreshPromise) {
    const { refreshToken, userId } = getAuthData() || {};
    if (!refreshToken) {
      // no refresh token available; behave like an auth failure
      return Promise.reject(new Error('No refresh token'));
    }

    refreshPromise = fetch(`${API_URL}/auth-token-refresh`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        refreshToken,
        userId,
      }),
    })
      .then(async (res) => {
        const data = await res.json();
        if (!res.ok || !data?.accessToken) {
          throw data || new Error('Refresh failed');
        }

        // persist new tokens
        saveAuthData({
          accessToken: data.accessToken,
          refreshToken: data.refreshToken,
          userId: data.userId,
        });

        // update axios default header for future requests
        axios.defaults.headers.common.Authorization = `${
          getAuthData().accessToken
        }`;
        return data;
      })
      .finally(() => {
        // allow a new refresh later
        refreshPromise = null;
      });
  }
  return refreshPromise;
}

axios.interceptors.request.use(
  (config) => {
    const customConfig = { ...config };
    const { accessToken } = getAuthData();
    if (accessToken) {
      customConfig.headers.Authorization = `${accessToken}`;
    }
    // config.headers['Content-Type'] = 'application/json';
    return customConfig;
  },
  (error) => {
    Promise.reject(error);
  }
);

axios.interceptors.response.use(
  (response) => {
    return response;
  },
  async (error) => {
    if (error.response?.status === 402 && error.response?.data?.code === 'TRIAL_EXPIRED') {
      try {
        useUiStore.getState().setTrialPaywallOpen(true);
      } catch (e) {
        // ignore
      }
      return Promise.reject(error);
    }

    const originalRequest = error.config;
    if (error.response?.status === 401 && !originalRequest._retry) {
      // Skip redirect for anonymous users viewing shared meetings
      // Allow 401 errors for /me endpoint when user is anonymous (shared meeting flow)
      const isAnonymousUser = !getAuthData()?.refreshToken;
      const isMeEndpoint = originalRequest.url?.includes('/users/me') || originalRequest.url?.endsWith('/me');
      
      if (isAnonymousUser && isMeEndpoint) {
        // For anonymous users, just reject the error without redirecting
        // This allows shared meeting pages to work without authentication
        return Promise.reject(error);
      }
      
      originalRequest._retry = true;
      try {
        await refreshTokenOnce();
        // retry the original request with the fresh access token
        const { accessToken } = getAuthData() || {};
        originalRequest.headers = originalRequest.headers || {};
        originalRequest.headers.Authorization = `${accessToken}`;
        return axios(originalRequest);
      } catch (e) {
        // refresh failed -> log out
        clearAuthData();
        document.location.href = '/sign-in';
        return Promise.reject(e);
      }
    }
    return Promise.reject(error);
  }
);

// eslint-disable-next-line consistent-return
export async function signUp(
  { email, password, firstName, lastName, companyName, title } = {},
  socket
) {
  try {
    const response = await axios.post(`/signup`, {
      email,
      password,
      firstName,
      lastName,
      companyName,
      title,
      timezone: localTimeZone,
    });
    saveAuthData(response.data);
    if (socket) {
      socket.auth.token = response.data.accessToken;
      socket.disconnect().connect();
    }
  } catch (error) {
    console.error(error);
    throw new Error(error?.response?.data?.message);
  }
}

export async function login(email, password, socket) {
  try {
    const response = await axios.post(`/login`, {
      email,
      password,
      timezone: localTimeZone,
    });
    saveAuthData(response.data);
    socket.auth.token = response.data.accessToken;
    socket.disconnect().connect();
    return response.data; // Return response data so caller can access role, etc.
  } catch (error) {
    console.error(error);
    throw new Error(error?.response?.data?.message);
  }
}

export function logout(callback) {
  clearAuthData();
  if (callback) {
    callback();
  }
}
